import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, Info, ShieldCheck, Clock, Package } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
// Mock product data based on the spec
const productData = {
  sku: 'BST1SLC',
  name: 'Sleeveless Crew Neck No Button Baseball Jersey',
  category: 'Baseball',
  categorySlug: 'baseball',
  image: 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRSBSSL01+POLYDEX+FRONT.png&h=600&site=12118&user=6101&w=600',
  description: 'A premium sleeveless no-button baseball jersey built for maximum mobility and comfort. Designed for team dealers and decorators who need high-quality, private-label blanks or fully sublimated custom uniforms.',
  details: ['Comfortable lightweight fabrics for moisture management', 'Double-stitched hems for durability and performance', 'Scoop bottom back', 'Optional laser perforation'],
  sizes: 'Male Y2XS–YXL, AS–A5XL',
  fabrics: ['Kiana', 'Poly180', 'Protech', 'CoolDry', 'Pinwick', 'Polydex', 'Micro-Knit', 'Fullmax', 'Flextech', 'Interfuse'],
  sizeKitSku: 'SK-BST1SLC'
};
export const ProductPage = () => {
  const {
    sku
  } = useParams<{
    sku: string;
  }>();
  const [activeTab, setActiveTab] = useState('details');
  const tabs = [{
    id: 'details',
    label: 'Product Details'
  }, {
    id: 'fabric',
    label: 'Fabric & Fit'
  }, {
    id: 'customization',
    label: 'Customization Options'
  }, {
    id: 'sizes',
    label: 'Available Sizes'
  }, {
    id: 'samples',
    label: 'Samples & Sizing Kits'
  }, {
    id: 'support',
    label: 'Wholesale Support'
  }];
  return <div className="bg-white min-h-screen flex flex-col" data-id="element-901">
      {/* Breadcrumbs */}
      <div className="bg-gray50 border-b border-gray200 py-3" data-id="element-902">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-2 text-sm text-gray-500" data-id="element-903">
          <Link to="/" className="hover:text-charcoal transition-colors" data-id="element-904">
            Home
          </Link>
          <ChevronRight className="w-4 h-4" data-id="element-905" />
          <Link to={`/category/${productData.categorySlug}`} className="hover:text-charcoal transition-colors" data-id="element-906">
            {productData.category}
          </Link>
          <ChevronRight className="w-4 h-4" data-id="element-907" />
          <span className="text-charcoal font-semibold truncate max-w-[200px] sm:max-w-none" data-id="element-908">
            {productData.name}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 lg:py-16 w-full" data-id="element-909">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start" data-id="element-910">
          {/* Product Image Gallery */}
          <div className="bg-gray50 rounded-xl border border-gray200 p-8 flex items-center justify-center relative sticky top-24" data-id="element-911">
            <div className="absolute top-4 left-4 bg-white px-3 py-1.5 text-xs font-mono font-bold text-gray-500 rounded border border-gray200 shadow-sm" data-id="element-912">
              SKU: {productData.sku}
            </div>
            <img src={productData.image} alt={productData.name} className="w-full max-w-md h-auto object-contain mix-blend-multiply" data-id="element-913" />
          </div>

          {/* Product Info & Actions */}
          <div className="flex flex-col" data-id="element-914">
            <div className="mb-8" data-id="element-915">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-ink mb-4 leading-tight" data-id="element-916">
                {productData.name}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-917">
                {productData.description}
              </p>
            </div>

            {/* Primary Actions */}
            <div className="bg-gray50 border border-gray200 rounded-xl p-6 mb-10" data-id="element-918">
              <div className="flex flex-col gap-4" data-id="element-919">
                <Link to="/products" className="w-full bg-brandRed hover:bg-brandRedDark text-white font-bold py-4 px-6 rounded-sm text-center transition-colors shadow-lg shadow-brandRed/20 text-lg flex items-center justify-center gap-2" data-id="element-920">
                  Start Your Design{' '}
                  <span className="font-normal text-sm opacity-90" data-id="element-921">
                    — See it Instantly in 3D
                  </span>
                </Link>
                <Link to="/wholesale-apply" className="w-full bg-white border-2 border-charcoal hover:bg-charcoal hover:text-white text-charcoal font-bold py-3.5 px-6 rounded-sm text-center transition-colors flex items-center justify-center gap-2" data-id="element-922">
                  Request Wholesale Support
                </Link>
              </div>
              <div className="mt-4 flex items-start gap-2 text-sm text-gray-500 bg-white p-3 rounded border border-gray100" data-id="element-923">
                <Info className="w-4 h-4 text-brandRed shrink-0 mt-0.5" data-id="element-924" />
                <p data-id="element-925">
                  Wholesale/account access may be required for ordering.
                  Combined quantity pricing available for dealers.
                </p>
              </div>
            </div>

            {/* Quick Specs */}
            <div className="grid grid-cols-2 gap-4 mb-10" data-id="element-926">
              <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-927">
                <ShieldCheck className="w-5 h-5 text-gray-400" data-id="element-928" />
                <span data-id="element-929">Private-label ready</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-930">
                <Clock className="w-5 h-5 text-gray-400" data-id="element-931" />
                <span data-id="element-932">Speed Rush available</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-933">
                <Package className="w-5 h-5 text-gray-400" data-id="element-934" />
                <span data-id="element-935">Drop-ship friendly</span>
              </div>
            </div>

            {/* Tabbed Content */}
            <div className="border border-gray200 rounded-xl overflow-hidden bg-white" data-id="element-936">
              <div className="flex overflow-x-auto border-b border-gray200 hide-scrollbar bg-gray50" data-id="element-937">
                {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('px-6 py-4 text-sm font-semibold whitespace-nowrap transition-colors relative', activeTab === tab.id ? 'text-brandRed bg-white' : 'text-gray-500 hover:text-charcoal hover:bg-gray100/50')} data-id="element-938">
                    {tab.label}
                    {activeTab === tab.id && <motion.div layoutId="activeProductTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brandRed" data-id="element-939" />}
                  </button>)}
              </div>

              <div className="p-6 md:p-8 min-h-[300px]" data-id="element-940">
                <AnimatePresence mode="wait" data-id="element-941">
                  <motion.div key={activeTab} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} data-id="element-942">
                    {activeTab === 'details' && <div className="space-y-4" data-id="element-943">
                        <h3 className="font-heading font-bold text-lg text-ink" data-id="element-944">
                          Product Features
                        </h3>
                        <ul className="list-disc pl-5 space-y-2 text-gray-600" data-id="element-945">
                          {productData.details.map((detail, i) => <li key={i} data-id="element-946">{detail}</li>)}
                        </ul>
                      </div>}

                    {activeTab === 'fabric' && <div className="space-y-6" data-id="element-947">
                        <div data-id="element-948">
                          <h3 className="font-heading font-bold text-lg text-ink mb-3" data-id="element-949">
                            Available Fabrics
                          </h3>
                          <div className="flex flex-wrap gap-2" data-id="element-950">
                            {productData.fabrics.map((fabric, i) => <span key={i} className="px-3 py-1 bg-gray100 border border-gray200 rounded text-sm text-charcoal font-medium" data-id="element-951">
                                {fabric}
                              </span>)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-952">
                          Our fabrics are engineered for durability, moisture
                          management, and vibrant sublimation printing. Contact
                          support for fabric recommendations based on your
                          specific sport and performance needs.
                        </p>
                      </div>}

                    {activeTab === 'customization' && <div className="space-y-4" data-id="element-953">
                        <h3 className="font-heading font-bold text-lg text-ink" data-id="element-954">
                          Customization Capabilities
                        </h3>
                        <p className="text-gray-600" data-id="element-955">
                          This item is available for full sublimation or as a
                          blank for your own decoration.
                        </p>
                        <ul className="space-y-3 mt-4" data-id="element-956">
                          <li className="flex items-start gap-3 text-sm text-gray-600" data-id="element-957">
                            <div className="w-1.5 h-1.5 rounded-full bg-brandRed mt-1.5 shrink-0" data-id="element-958"></div>
                            <span data-id="element-959">
                              <strong data-id="element-960">Full Sublimation:</strong> Edge-to-edge
                              printing with unlimited colors, logos, and numbers
                              included in wholesale pricing.
                            </span>
                          </li>
                          <li className="flex items-start gap-3 text-sm text-gray-600" data-id="element-961">
                            <div className="w-1.5 h-1.5 rounded-full bg-brandRed mt-1.5 shrink-0" data-id="element-962"></div>
                            <span data-id="element-963">
                              <strong data-id="element-964">Private Label:</strong> No manufacturer
                              logos visible. Built to protect your brand.
                            </span>
                          </li>
                        </ul>
                        <div className="mt-6" data-id="element-965">
                          <Link to="/products" className="text-brandRed font-bold hover:text-brandRedDark flex items-center gap-1 text-sm" data-id="element-966">
                            Start Your Design Online{' '}
                            <ChevronRight className="w-4 h-4" data-id="element-967" />
                          </Link>
                        </div>
                      </div>}

                    {activeTab === 'sizes' && <div className="space-y-4" data-id="element-968">
                        <h3 className="font-heading font-bold text-lg text-ink" data-id="element-969">
                          Size Range
                        </h3>
                        <div className="bg-gray50 border border-gray200 rounded-lg p-4" data-id="element-970">
                          <p className="font-mono text-charcoal font-bold" data-id="element-971">
                            {productData.sizes}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-4" data-id="element-972">
                          Detailed size charts are available in the dealer
                          portal. We strongly recommend ordering a size kit for
                          accurate team fittings.
                        </p>
                      </div>}

                    {activeTab === 'samples' && <div className="space-y-4" data-id="element-973">
                        <h3 className="font-heading font-bold text-lg text-ink" data-id="element-974">
                          Samples & Sizing Kits
                        </h3>
                        <p className="text-gray-600" data-id="element-975">
                          Ensure perfect fits and demonstrate quality to your
                          customers before placing a full team order.
                        </p>
                        <div className="bg-gray50 border border-gray200 rounded-lg p-4 flex items-center justify-between mt-4" data-id="element-976">
                          <div data-id="element-977">
                            <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-978">
                              Size Kit SKU
                            </span>
                            <span className="font-mono text-charcoal font-bold" data-id="element-979">
                              {productData.sizeKitSku}
                            </span>
                          </div>
                          <Link to="/wholesale-apply" className="text-sm font-bold text-brandRed hover:text-brandRedDark" data-id="element-980">
                            Request Kit
                          </Link>
                        </div>
                      </div>}

                    {activeTab === 'support' && <div className="space-y-4" data-id="element-981">
                        <h3 className="font-heading font-bold text-lg text-ink" data-id="element-982">
                          Dealer Production Support
                        </h3>
                        <p className="text-gray-600" data-id="element-983">
                          Our T1 production team provides comprehensive support
                          for this item, including:
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-id="element-984">
                          {['Art review & setup', 'Color matching', 'Full-size sample review', 'Drop-ship fulfillment'].map((item, i) => <li key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-gray50 px-3 py-2 rounded border border-gray100" data-id="element-985">
                              <ShieldCheck className="w-4 h-4 text-brandRed" data-id="element-986" />{' '}
                              {item}
                            </li>)}
                        </ul>
                      </div>}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};